import { forwardRef, useId, cloneElement, isValidElement, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode, type ReactElement } from 'react';
import { cn } from '../cn';
import styles from './Input.module.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(styles.input, invalid && styles.invalid, className)} {...rest} />;
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(styles.input, styles.textarea, invalid && styles.invalid, className)}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn(styles.input, styles.select, invalid && styles.invalid, className)} {...rest}>
      {children}
    </select>
  );
});

export interface FormFieldProps {
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormField({ label, help, error, children, className }: FormFieldProps) {
  const generatedId = useId();
  // If the child is a single element without an id, inject one so the <label>
  // can target it. This makes Playwright's `getByLabel(...)` and native
  // label-click behavior both work.
  let labeledChild = children;
  let inputId: string | undefined;
  if (isValidElement<{ id?: string }>(children)) {
    inputId = children.props.id ?? generatedId;
    if (!children.props.id) {
      labeledChild = cloneElement(children as ReactElement<{ id?: string }>, { id: inputId });
    }
  }
  return (
    <div className={cn(styles.field, error && styles.fieldError, className)}>
      {label && <label htmlFor={inputId} className={styles.label}>{label}</label>}
      {labeledChild}
      {error ? <span className={styles.err}>{error}</span> : help ? <span className={styles.help}>{help}</span> : null}
    </div>
  );
}
